'use client';

// The fields of the "Industry & activity" filter group, one component each.
// State stays in the sidebar; IndustryFilterGroup hands its props through.
import { Check, ChevronRight } from 'lucide-react';
import { cn } from '@/lib/utils';
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from '@/components/ui/command';
import { Popover } from '@/components/ui/popover';
import {
  FacetCount,
  FIELD_LABEL_CLASS,
  FIELD_PLACEHOLDER_CLASS,
  FilterLabel,
  INPUT_PLACEHOLDER_CLASS,
} from '../FilterControls';
import type { NaceCode } from '../searchSidebarOptions';
import {
  ComboboxContent,
  ComboboxTrigger,
  FilterChip,
  OptionCheck,
} from './FilterFieldParts';
import type { IndustryFilterGroupProps } from './IndustryFilterGroup';

/** High-level Industry filter (maps to NACE divisions). */
export function IndustryPickerField({
  t,
  lockOf,
  industryOpen,
  setIndustryOpen,
  staged,
  allIndustries,
  toggleIndustry,
  facetCount,
}: Pick<
  IndustryFilterGroupProps,
  | 't'
  | 'lockOf'
  | 'industryOpen'
  | 'setIndustryOpen'
  | 'staged'
  | 'allIndustries'
  | 'toggleIndustry'
  | 'facetCount'
>) {
  const { industries: selectedIndustries } = staged;
  return (
    <div className="space-y-1.5">
      <FilterLabel lock={lockOf('industries')} className={FIELD_LABEL_CLASS}>
        {t('search.sidebar.industry', { defaultValue: 'Industry' })}
      </FilterLabel>
      <Popover
        open={lockOf('industries') ? false : industryOpen}
        onOpenChange={setIndustryOpen}
      >
        <ComboboxTrigger disabled={!!lockOf('industries')}>
          {selectedIndustries.length === 0 ? (
            <span className={FIELD_PLACEHOLDER_CLASS}>
              {t('search.sidebar.industryPlaceholder', {
                defaultValue: 'Pick industry',
              })}
            </span>
          ) : (
            selectedIndustries.map((key) => {
              const cat = allIndustries.find((c) => c.key === key);
              return (
                <FilterChip key={key} onRemove={() => toggleIndustry(key)}>
                  <span className="max-w-[220px] truncate">
                    {cat?.label ?? key}
                  </span>
                </FilterChip>
              );
            })
          )}
        </ComboboxTrigger>
        <ComboboxContent>
          <Command>
            <CommandInput
              className={`text-xs ${INPUT_PLACEHOLDER_CLASS}`}
              placeholder={t('search.sidebar.searchIndustries', {
                defaultValue: 'Search industries…',
              })}
            />
            <CommandList className="max-h-[272px] overflow-y-auto">
              <CommandEmpty>
                {t('search.sidebar.noIndustryFound', {
                  defaultValue: 'No industry found.',
                })}
              </CommandEmpty>
              <CommandGroup>
                {allIndustries.map(({ key, label }) => (
                  <CommandItem
                    key={key}
                    value={label}
                    onSelect={() => toggleIndustry(key)}
                  >
                    <OptionCheck selected={selectedIndustries.includes(key)} />
                    <span className="truncate text-xs">{label}</span>
                    <FacetCount count={facetCount('industry', key)} />
                  </CommandItem>
                ))}
              </CommandGroup>
            </CommandList>
          </Command>
        </ComboboxContent>
      </Popover>
    </div>
  );
}

type NaceFieldProps = Pick<
  IndustryFilterGroupProps,
  | 't'
  | 'lockOf'
  | 'facetCount'
  | 'naceCodesOpen'
  | 'setNaceCodesOpen'
  | 'setNaceSearch'
  | 'staged'
  | 'naceCodes'
  | 'removeSet'
  | 'set'
  | 'naceSearch'
  | 'naceListRef'
  | 'filteredNaceCodes'
  | 'naceCodesHaveChildren'
  | 'expandedNaceNodes'
  | 'toggleSet'
  | 'toggleNaceExpanded'
>;

/** WZ 2025 Code (sector tree picker). */
export function NaceCodeField({
  t,
  lockOf,
  naceCodesOpen,
  setNaceCodesOpen,
  setNaceSearch,
  staged,
  naceCodes,
  removeSet,
  set,
  naceSearch,
  naceListRef,
  filteredNaceCodes,
  naceCodesHaveChildren,
  expandedNaceNodes,
  toggleSet,
  toggleNaceExpanded,
  facetCount,
}: NaceFieldProps) {
  const { naceCodes: selectedNaceCodes } = staged;
  return (
    <div className="space-y-1.5">
      <FilterLabel lock={lockOf('naceCodes')} className={FIELD_LABEL_CLASS}>
        {t('search.sidebar.naceCode', { defaultValue: 'WZ 2025 code' })}
      </FilterLabel>
      <Popover
        open={lockOf('naceCodes') ? false : naceCodesOpen}
        onOpenChange={(open) => {
          setNaceCodesOpen(open);
          if (!open) setNaceSearch('');
        }}
      >
        <ComboboxTrigger disabled={!!lockOf('naceCodes')}>
          {selectedNaceCodes.length === 0 ? (
            <span className={FIELD_PLACEHOLDER_CLASS}>
              {t('search.sidebar.industriesPlaceholder')}
            </span>
          ) : (
            selectedNaceCodes.map((code) => {
              const nace = naceCodes.find((c) => c.code === code);
              return (
                <FilterChip
                  key={code}
                  onRemove={() => removeSet(code, set.naceCodes)}
                >
                  <span className="font-mono text-muted-foreground">
                    {code}
                  </span>
                  <span className="max-w-[220px] truncate">
                    {nace?.description || ''}
                  </span>
                </FilterChip>
              );
            })
          )}
        </ComboboxTrigger>
        <ComboboxContent>
          <Command shouldFilter={false}>
            <CommandInput
              className={`text-xs ${INPUT_PLACEHOLDER_CLASS}`}
              placeholder={t('search.sidebar.searchSectors')}
              value={naceSearch}
              onValueChange={(v) => {
                setNaceSearch(v);
                if (naceListRef.current) naceListRef.current.scrollTop = 0;
              }}
            />
            <CommandList
              ref={naceListRef}
              className="h-[272px] overflow-y-auto"
            >
              <CommandEmpty>{t('search.sidebar.noSectorFound')}</CommandEmpty>
              <CommandGroup>
                {filteredNaceCodes.map((nace) => (
                  <NaceCodeOption
                    key={nace.code}
                    t={t}
                    nace={nace}
                    naceSearch={naceSearch}
                    staged={staged}
                    set={set}
                    naceCodesHaveChildren={naceCodesHaveChildren}
                    expandedNaceNodes={expandedNaceNodes}
                    toggleSet={toggleSet}
                    toggleNaceExpanded={toggleNaceExpanded}
                    facetCount={facetCount}
                  />
                ))}
              </CommandGroup>
            </CommandList>
          </Command>
        </ComboboxContent>
      </Popover>
    </div>
  );
}

/** One row of the WZ tree: expand toggle (tree mode only), check mark, code,
 *  description and count. Below two typed characters the list is a flat
 *  search result, so rows lose their indent and toggle. */
function NaceCodeOption({
  t,
  nace,
  naceSearch,
  naceCodesHaveChildren,
  expandedNaceNodes,
  toggleSet,
  set,
  toggleNaceExpanded,
  staged,
  facetCount,
}: Pick<
  NaceFieldProps,
  | 't'
  | 'naceSearch'
  | 'naceCodesHaveChildren'
  | 'expandedNaceNodes'
  | 'toggleSet'
  | 'set'
  | 'toggleNaceExpanded'
  | 'staged'
  | 'facetCount'
> & { nace: NaceCode }) {
  const { naceCodes: selectedNaceCodes } = staged;
  const inTreeMode = naceSearch.trim().length < 2;
  const hasChildren = naceCodesHaveChildren.has(nace.code);
  const isExpanded = expandedNaceNodes.has(nace.code);
  const indent = inTreeMode ? (nace.level - 1) * 12 : 0;
  return (
    <CommandItem
      value={`${nace.code} ${nace.description}`}
      onSelect={() => toggleSet(nace.code, set.naceCodes)}
    >
      <div className="flex items-center w-full" style={{ paddingLeft: indent }}>
        {inTreeMode && hasChildren ? (
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              toggleNaceExpanded(nace.code);
            }}
            aria-label={
              isExpanded
                ? t('search.sidebar.collapse', {
                    defaultValue: 'Collapse',
                  })
                : t('search.sidebar.expand', {
                    defaultValue: 'Expand',
                  })
            }
            className="mr-1 flex h-4 w-4 items-center justify-center rounded hover:bg-muted shrink-0"
          >
            <ChevronRight
              size={12}
              className={cn('transition-transform', isExpanded && 'rotate-90')}
            />
          </button>
        ) : (
          <span className="mr-1 inline-block h-4 w-4 shrink-0" />
        )}
        <Check
          className={cn(
            'mr-2 h-4 w-4 shrink-0',
            selectedNaceCodes.includes(nace.code) ? 'opacity-100' : 'opacity-0',
          )}
        />
        <span className="font-mono text-xs text-muted-foreground mr-1.5 shrink-0">
          {nace.code}
        </span>
        <span className="truncate text-xs">{nace.description}</span>
        <FacetCount count={facetCount('nace', nace.code)} />
      </div>
    </CommandItem>
  );
}

/** Tags (products, risks, opportunities). */
export function TagsField({
  t,
  lockOf,
  tagsOpen,
  setTagsOpen,
  setTagSearch,
  staged,
  removeSet,
  set,
  tagSearch,
  tagList,
  toggleSet,
}: Pick<
  IndustryFilterGroupProps,
  | 't'
  | 'lockOf'
  | 'tagsOpen'
  | 'setTagsOpen'
  | 'setTagSearch'
  | 'staged'
  | 'removeSet'
  | 'set'
  | 'tagSearch'
  | 'tagList'
  | 'toggleSet'
>) {
  const { tags } = staged;
  return (
    <div className="space-y-1.5">
      <FilterLabel lock={lockOf('tags')} className={FIELD_LABEL_CLASS}>
        {t('search.sidebar.tags', {
          defaultValue: 'Tags',
        })}
      </FilterLabel>
      <Popover
        open={lockOf('tags') ? false : tagsOpen}
        onOpenChange={(open) => {
          setTagsOpen(open);
          if (!open) setTagSearch('');
        }}
      >
        <ComboboxTrigger disabled={!!lockOf('tags')}>
          {tags.length === 0 ? (
            <span className={FIELD_PLACEHOLDER_CLASS}>
              {t('search.sidebar.tagsPlaceholder', {
                defaultValue: 'e.g. Software, Supply chains, Consulting…',
              })}
            </span>
          ) : (
            tags.map((kw) => (
              <FilterChip key={kw} onRemove={() => removeSet(kw, set.tags)}>
                <span className="max-w-[220px] truncate">{kw}</span>
              </FilterChip>
            ))
          )}
        </ComboboxTrigger>
        <ComboboxContent>
          <Command shouldFilter={false}>
            <CommandInput
              className={INPUT_PLACEHOLDER_CLASS}
              placeholder={t('search.sidebar.searchTags', {
                defaultValue: 'Search tags...',
              })}
              value={tagSearch}
              onValueChange={setTagSearch}
            />
            <CommandList className="max-h-[272px]">
              <CommandEmpty>
                {t('search.sidebar.noTagFound', {
                  defaultValue: 'No tag found.',
                })}
              </CommandEmpty>
              <CommandGroup>
                {tagList
                  .filter(
                    (kw) =>
                      !tagSearch ||
                      kw.toLowerCase().includes(tagSearch.toLowerCase()),
                  )
                  .slice(0, 100)
                  .map((kw) => (
                    <CommandItem
                      key={kw}
                      value={kw}
                      onSelect={() => toggleSet(kw, set.tags)}
                    >
                      <OptionCheck selected={tags.includes(kw)} />
                      <span className="truncate text-xs">{kw}</span>
                    </CommandItem>
                  ))}
              </CommandGroup>
            </CommandList>
          </Command>
        </ComboboxContent>
      </Popover>
    </div>
  );
}

/** CPV-Code — single value; picking one closes the picker. */
export function CpvCodeField({
  t,
  lockOf,
  cpvCodesOpen,
  setCpvCodesOpen,
  setCpvSearch,
  staged,
  cpvCodesList,
  set,
  cpvSearch,
  cpvListRef,
}: Pick<
  IndustryFilterGroupProps,
  | 't'
  | 'lockOf'
  | 'cpvCodesOpen'
  | 'setCpvCodesOpen'
  | 'setCpvSearch'
  | 'staged'
  | 'cpvCodesList'
  | 'set'
  | 'cpvSearch'
  | 'cpvListRef'
>) {
  const { cpvCode } = staged;
  return (
    <div className="space-y-1.5">
      <FilterLabel lock={lockOf('cpvCode')} className={FIELD_LABEL_CLASS}>
        {t('search.sidebar.cpvCode', { defaultValue: 'CPV code' })}
      </FilterLabel>
      <Popover
        open={lockOf('cpvCode') ? false : cpvCodesOpen}
        onOpenChange={(open) => {
          setCpvCodesOpen(open);
          if (!open) setCpvSearch('');
        }}
      >
        <ComboboxTrigger disabled={!!lockOf('cpvCode')}>
          {!cpvCode ? (
            <span className={FIELD_PLACEHOLDER_CLASS}>
              {t('search.sidebar.cpvCodePlaceholder', {
                defaultValue: 'e.g. 45000000 Construction work',
              })}
            </span>
          ) : (
            <FilterChip onRemove={() => set.cpvCode('')}>
              <span className="font-mono text-muted-foreground">{cpvCode}</span>
              <span className="max-w-[180px] truncate">
                {cpvCodesList.find((c) => c.code === cpvCode)?.description ||
                  ''}
              </span>
            </FilterChip>
          )}
        </ComboboxTrigger>
        <ComboboxContent>
          <Command shouldFilter={false}>
            <CommandInput
              className={`text-xs ${INPUT_PLACEHOLDER_CLASS}`}
              placeholder={t('search.sidebar.searchCpvCodes', {
                defaultValue: 'Search CPV codes (min. 2 digits)…',
              })}
              value={cpvSearch}
              onValueChange={(v) => {
                setCpvSearch(v);
                if (cpvListRef.current) cpvListRef.current.scrollTop = 0;
              }}
            />
            <CommandList ref={cpvListRef} className="h-[272px] overflow-y-auto">
              <CommandEmpty>
                {t('search.sidebar.noCpvFound', {
                  defaultValue: 'No CPV code found.',
                })}
              </CommandEmpty>
              <CommandGroup>
                {cpvCodesList.map((cpv) => (
                  <CommandItem
                    key={cpv.code}
                    value={`${cpv.code} ${cpv.description}`}
                    onSelect={() => {
                      set.cpvCode(cpv.code);
                      setCpvCodesOpen(false);
                      setCpvSearch('');
                    }}
                  >
                    <OptionCheck selected={cpvCode === cpv.code} />
                    <span className="font-mono text-xs text-muted-foreground mr-1.5">
                      {cpv.code}
                    </span>
                    <span className="truncate text-xs">{cpv.description}</span>
                  </CommandItem>
                ))}
              </CommandGroup>
            </CommandList>
          </Command>
        </ComboboxContent>
      </Popover>
    </div>
  );
}
